import React, { Component, createRef, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import has from 'lodash/has';
import keycode from 'keycode';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { ExperienceSuccess } from '@atlassian/ufo';
import { loadChannelsExperience } from '../experiences.tsx';
import messages from './messages.tsx';
import { ChannelsTitle } from './title/index.tsx';

export type Props = {
	portalId: number;
	isLoading: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	EmailChannel: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	WidgetChannel: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HelpDeskChannel: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ChatChannel: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	CustomerAccessChannel: ComponentType<Record<any, any>>;
	onSetIsChannelsDialogOpen: (isOpen: boolean) => void;
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class ChannelsJsdViewComponent extends Component<Props> {
	static defaultProps = {
		isLoading: true,
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	constructor(props: any) {
		super(props);
		this.node = createRef();
	}

	componentDidMount() {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('click', this.onPageClick);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('keydown', this.onKeyPress, false);
	}

	componentWillUnmount() {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.removeEventListener('click', this.onPageClick);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.removeEventListener('keydown', this.onKeyPress, false);
	}

	onPageClick = (event: MouseEvent) => {
		if (has(this, 'node.current') && !this.node.current.contains(event.target)) {
			this.props.onSetIsChannelsDialogOpen(false);
		}
	};

	onKeyPress = (event: KeyboardEvent) => {
		if (event.keyCode === keycode('escape')) {
			this.props.onSetIsChannelsDialogOpen(false);
		}
	};

	isChannelInfoLoaded() {
		return this.props.portalId !== 0;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	node: any;

	render() {
		const {
			intl,
			isLoading,
			EmailChannel,
			HelpDeskChannel,
			WidgetChannel,
			ChatChannel,
			CustomerAccessChannel,
		} = this.props;

		return (
			<Box xcss={getWillShowNav4() && nav4SpinnerStyles} ref={this.node}>
				{isLoading ? (
					<Box xcss={[spinnerWrapperStyles, !getWillShowNav4() && nav3SpinnerStyles]}>
						<Spinner size="large" />
					</Box>
				) : (
					<>
						<ChannelsTitle title={intl.formatMessage(messages.title)} />
						<EmailChannel />
						<HelpDeskChannel />
						<WidgetChannel />
						<ChatChannel />
						<HorizontalLine role="presentation" />
						<CustomerAccessChannel />
						<ViewTracker />

						{this.isChannelInfoLoaded() && (
							<ExperienceSuccess experience={loadChannelsExperience} />
						)}
					</>
				)}
			</Box>
		);
	}
}

export const ChannelsJsdView = injectIntl(ChannelsJsdViewComponent);

const spinnerWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '296px',
});

const nav3SpinnerStyles = xcss({
	width: '416px',
});

const nav4SpinnerStyles = xcss({
	minWidth: '280px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalLine = styled.hr({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.border'),
	borderWidth: '1px',
	borderStyle: 'solid',
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: token('space.100'),
	marginLeft: 0,
});
